const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "http://localhost:8787";

// Comics come from Metron through the worker's /comics/* routes (see
// media-tracker-proxy/README.md). Each call costs one Metron request out of a
// 20/min, 5000/day budget, so screens fetch on demand only. A 429 carries
// `retryAfter` (seconds) so callers can say how long to wait.

async function request(path, opts = {}) {
	return (await requestWithMeta(path, opts)).data;
}

// Metron budget + edge-cache status from the worker's (CORS-exposed) headers.
// null for a header the response didn't carry (an edge-cache HIT has no budget).
function metaOf(resp) {
	const num = (name) => {
		const v = resp.headers.get(name);
		return v == null || v === "" ? null : Number(v);
	};
	return {
		cache: resp.headers.get("X-Cache"),
		burstRemaining: num("X-Metron-Burst-Remaining"),
		burstReset: num("X-Metron-Burst-Reset"),
		dailyRemaining: num("X-Metron-Daily-Remaining"),
	};
}

// The response body plus metaOf(): the background role fetcher paces itself on it.
async function requestWithMeta(path, { signal } = {}) {
	const resp = await fetch(`${WORKER_URL}${path}`, { signal });
	if (!resp.ok) {
		let message = `Request failed: ${resp.status}`;
		try {
			const body = await resp.json();
			if (body?.error) message = body.error;
		} catch {
			// non-JSON body, keep default message
		}
		const err = new Error(message);
		err.status = resp.status;
		const retry = Number(resp.headers.get("Retry-After"));
		if (Number.isFinite(retry)) err.retryAfter = retry;
		throw err;
	}
	return { data: await resp.json(), meta: metaOf(resp) };
}

// A friendly line for a failed Metron call.
export function comicsErrorMessage(err) {
	if (err?.status === 429) {
		const s = err.retryAfter;
		return s ? `Metron is busy — try again in ${s}s.` : "Metron is busy — try again in a minute.";
	}
	return err?.message ?? String(err);
}

// Issue search. "X-Men (2004) #188", "X-Men #188" and "X-Men 188" all narrow to
// series + number. Returns { count, page, next_page, results: [issue rows] }.
export function searchComics(query, { page = 1, signal } = {}) {
	const params = new URLSearchParams({ q: query });
	if (page > 1) params.set("page", String(page));
	return request(`/comics/search?${params}`, { signal });
}

// One issue (or Metron collected edition) in full: series, format, credits,
// reprints, isbn…
export function getComic(issueId, { signal } = {}) {
	return request(`/comic/${encodeURIComponent(issueId)}`, { signal });
}

// Series search (for the "Collects…" picker). Returns a page of
// { series_id, label, year_began, issue_count, publisher, type, format }.
export function searchComicSeries(query, { page = 1, signal } = {}) {
	const params = new URLSearchParams({ q: query });
	if (page > 1) params.set("page", String(page));
	return request(`/comics/series?${params}`, { signal });
}

// Issue detail with the budget/cache meta — for the paced background fetcher.
export function getComicWithMeta(issueId, { signal } = {}) {
	return requestWithMeta(`/comic/${encodeURIComponent(issueId)}`, { signal });
}

// ---------- creators ----------

// Creator search by name → [{ creator_id, name }].
export function searchComicCreators(query, { signal } = {}) {
	const params = new URLSearchParams({ q: query });
	return request(`/comics/creators?${params}`, { signal });
}

// Creator detail: { creator_id, name, image, summary, birth, death, alias, … }.
export function getComicCreator(creatorId, { signal } = {}) {
	return request(`/comics/creator/${encodeURIComponent(creatorId)}`, { signal });
}

// One page (100) of a creator's credited issues — list rows, no credits —
// with meta. { data: { count, page, next_page, results }, meta }.
export function getComicCreatorIssues(creatorId, { page = 1, signal } = {}) {
	return requestWithMeta(
		`/comics/creator/${encodeURIComponent(creatorId)}/issues?page=${page}`,
		{ signal },
	);
}

// ---------- series ----------

// Series detail: { series_id, name, label, status, publisher, type, format,
// issue_count, associated: [{ series_id, label }], … }.
export function getComicSeries(seriesId, { signal } = {}) {
	return request(`/comics/series/${encodeURIComponent(seriesId)}`, { signal });
}

// One page of a series' issues, with meta. `after` (YYYY-MM-DD) narrows to
// issues on sale from that date — the subscription check.
export function getComicSeriesIssues(seriesId, { page = 1, after = null, signal } = {}) {
	const params = new URLSearchParams({ page: String(page) });
	if (after) params.set("after", after);
	return requestWithMeta(`/comics/series/${encodeURIComponent(seriesId)}/issues?${params}`, { signal });
}
